<script>
import { AlertServices } from "@/services/AlertServices";
import { mapActions, mapGetters, mapMutations } from "vuex";
import DownloadExcelLoading from "@/components/elements/DownloadExcelLoading.vue";
import { DateService } from "@/services/Utilities/DateService";
import { vueAppMixin } from "@/mixins/vueAppMixin";
import { dataMixin } from "@/mixins/dataMixin";
import { gateMixin } from "@/mixins/gateMixin";
import { paginationMixin } from "@/mixins/paginationMixin";
import FindCrewModal from "@/components/sub_components/reports/mmce/FindCrewModal.vue";
import { CrewService } from "@/services/CrewService";

// Constants
const ROLES = {
  MASTER: "master",
  CHIEF_ENGINEER: "chief_engineer",
};

const RELIEVER_FIELDS = [
  "reliever_id",
  "reliever_name",
  "reliever_age",
  "reliever_ex_vessel",
];

export default {
  name: "GenerateMmce",
  components: {
    FindCrewModal,
    DownloadExcelLoading,
  },
  emits: ["update_visibility"],
  mixins: [vueAppMixin, dataMixin, gateMixin, paginationMixin],

  data() {
    return {
      reportData: [],
      originalReportData: [],
      toEdit: false,
      todayDate: DateService.parseEltDateFormat(DateService.dateToday()),
      isSaving: false,
      isLoadingData: false,
      showModal: false,
      selectedRankId: null,
      modalContext: {
        vesselIndex: null,
        role: null,
      },
      removedRelievers: [],
    };
  },

  computed: {
    ...mapGetters(["mmceDetails"]),
  },

  methods: {
    ...mapActions([
      "downloadMmceExcelReport",
      "getCrewList",
      "getMmceDetails",
      "saveMmceDetails",
    ]),
    ...mapMutations(["SET_MMCE_DETAIL", "SET_NEW_MMCE_DETAIL"]),

    // Generate report with error handling
    async generateReport() {
      try {
        this.isLoadingData = true;
        const mmceObject = await this.getMmceDetails();

        if (!mmceObject) {
          throw new Error("Failed to load MMCE data from backend");
        }

        this.reportData = this.mmceDetails;
        this.originalReportData = this.deepClone(this.reportData);
        this.resetRemovedRelievers();
      } catch (error) {
        this.handleError("Error loading MMCE data", error);
      } finally {
        this.isLoadingData = false;
      }
    },

    async downloadExcelReport() {
      document.querySelector("#LoadingModalExcel").style.display = "block";
      console.log("ping something into the bs");
      await this.downloadMmceExcelReport().then((r) => {
        if (!!r) {
          AlertServices.successAlert(
            "Exporting MMCE Report into excel successfully.",
            "MMCE Report"
          );
          document.querySelector("#LoadingModalExcel").style.display = "none";
        }
        document.querySelector("#LoadingModalExcel").style.display = "none";
      });
    },

    // Open crew selection modal
    openFindCrewModal(vesselIndex, role, selectedRank) {
      this.selectedRankId = selectedRank;
      this.modalContext = { vesselIndex, role };
      this.showModal = true;
    },

    // Remove reliever crew
    removeRelieverCrew(vesselIndex, role) {
      const vessel = this.reportData[vesselIndex];
      const target = vessel[role];
      const originalTarget = this.getOriginalTarget(vesselIndex, role);

      // Only track removal if reliever existed in original data
      if (this.shouldTrackRemoval(target, originalTarget)) {
        this.trackRelieverRemoval(vessel, role, originalTarget.reliever_id);
      }

      this.clearRelieverData(target);
    },

    // Handle crew selection
    async handleCrewSelect(crew) {
      try {
        const { vesselIndex, role } = this.modalContext;
        const [selectedCrewData, target, originalTarget] = await Promise.all([
          CrewService.getCrewInfo({ crewId: crew.id }),
          Promise.resolve(this.reportData[vesselIndex][role]),
          Promise.resolve(this.getOriginalTarget(vesselIndex, role)),
        ]);

        this.handleRelieverReplacement(
          vesselIndex,
          role,
          crew.id,
          originalTarget
        );

        this.updateCrewData(target, crew, selectedCrewData);
        this.showModal = false;
      } catch (error) {
        this.handleError("Error selecting crew", error);
      }
    },

    // Save MMCE list with validation
    async saveMmceList() {
      if (this.isSaving) return;

      try {
        this.isSaving = true;
        const savePayload = this.prepareSavePayload();
                const response = await this.saveMmceDetails(savePayload);

        if (!response) {
          throw new Error("Failed to save MMCE report data");
        }
        //TODO: load the mmce details again to refresh the data
        await this.$nextTick(async () => {
          this.handleSuccessfulSave();
          this.generateReport();
        });
      } catch (error) {
        this.handleError("Error saving MMCE data", error);
      } finally {
        this.isSaving = false;
      }
    },

    // Discard changes and reset state
    discardChanges() {
      this.reportData = this.deepClone(this.originalReportData);
      this.resetRemovedRelievers();
      this.toEdit = false;
    },

    // Utility method for nested property access
    get(obj, path, fallback = "") {
      return (
        path.split(".").reduce((acc, part) => acc?.[part], obj) || fallback
      );
    },

    // Helper methods
    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },

    resetRemovedRelievers() {
      this.removedRelievers = [];
    },

    getOriginalTarget(vesselIndex, role) {
      return this.originalReportData[vesselIndex]?.[role];
    },

    shouldTrackRemoval(target, originalTarget) {
      return target.reliever_id && originalTarget?.reliever_id;
    },

    clearRelieverData(target) {
      RELIEVER_FIELDS.forEach((field) => {
        target[field] = null;
      });
    },

    trackRelieverRemoval(vessel, role, originalReliverId) {
      const mmceId = vessel.mmce_id || vessel.id;
      const vesselId = vessel.vessel_id || vessel.vesselId;

      let existingEntry = this.findRemovedRelieverEntry(mmceId, vesselId);

      if (existingEntry) {
        existingEntry[role] = originalReliverId;
      } else {
        this.removedRelievers.push(
          this.createRemovedRelieverEntry(
            mmceId,
            vesselId,
            role,
            originalReliverId
          )
        );
      }
    },

    findRemovedRelieverEntry(mmceId, vesselId) {
      return this.removedRelievers.find(
        (entry) => entry.mmce_id === mmceId && entry.vessel_id === vesselId
      );
    },

    createRemovedRelieverEntry(mmceId, vesselId, role, relieverId) {
      const entry = {
        mmce_id: mmceId,
        vessel_id: vesselId,
        [ROLES.MASTER]: null,
        [ROLES.CHIEF_ENGINEER]: null,
      };
      entry[role] = relieverId;
      return entry;
    },

    handleRelieverReplacement(vesselIndex, role, newCrewId, originalTarget) {
      const originalReliverId = originalTarget?.reliever_id;

      if (originalReliverId && originalReliverId !== newCrewId) {
        // Replacing original reliever with new one
        this.trackOriginalRelieverRemoval(vesselIndex, role, originalReliverId);
      } else if (originalReliverId === newCrewId) {
        // Selecting same crew as original - remove from removed list
        this.removeFromRemovedRelievers(vesselIndex, role);
      }
    },

    trackOriginalRelieverRemoval(vesselIndex, role, originalReliverId) {
      const vessel = this.reportData[vesselIndex];
      const mmceId = vessel.mmce_id || vessel.id;
      const vesselId = vessel.vessel_id || vessel.vesselId;

      let existingEntry = this.findRemovedRelieverEntry(mmceId, vesselId);

      if (existingEntry) {
        existingEntry[role] = originalReliverId;
      } else {
        this.removedRelievers.push(
          this.createRemovedRelieverEntry(
            mmceId,
            vesselId,
            role,
            originalReliverId
          )
        );
      }
    },

    removeFromRemovedRelievers(vesselIndex, role) {
      const vessel = this.reportData[vesselIndex];
      const mmceId = vessel.mmce_id || vessel.id;
      const vesselId = vessel.vessel_id || vessel.vesselId;

      const existingEntryIndex = this.removedRelievers.findIndex(
        (entry) => entry.mmce_id === mmceId && entry.vessel_id === vesselId
      );

      if (existingEntryIndex !== -1) {
        const existingEntry = this.removedRelievers[existingEntryIndex];
        existingEntry[role] = null;

        // Remove entry if both roles are null
        if (
          !existingEntry[ROLES.MASTER] &&
          !existingEntry[ROLES.CHIEF_ENGINEER]
        ) {
          this.removedRelievers.splice(existingEntryIndex, 1);
        }
      }
    },

    updateCrewData(target, crew, selectedCrewData) {
      const lastVessel =
        selectedCrewData.currentActivity.activityCodeName === "sailing"
          ? selectedCrewData.currentActivity.vesselName
          : selectedCrewData.lastVessel.vesselName;

      Object.assign(target, {
        reliever_id: crew.id,
        reliever_name: crew.fullName,
        reliever_age: { age: selectedCrewData.age },
        reliever_ex_vessel: lastVessel,
        remarks: target.remarks || "",
      });
    },

    prepareSavePayload() {
      const filteredRemovedRelievers = this.removedRelievers.filter(
        (entry) => entry[ROLES.MASTER] || entry[ROLES.CHIEF_ENGINEER]
      );

      return {
        reportData: this.reportData,
        removedRelievers: filteredRemovedRelievers,
      };
    },

    handleSuccessfulSave() {
      this.originalReportData = this.deepClone(this.reportData);
      this.resetRemovedRelievers();
      this.toEdit = false;
      AlertServices.successAlert(
        "MMCE report data saved successfully",
        "Success"
      );
    },

    handleError(message, error) {
      console.error(message, error);
      AlertServices.errorAlert(message, "Error");
    },
  },

  mounted() {
    this.generateReport();
  },
};
</script>

<template>
  <div class="row py-2">
    <div
      class="col-12 text-center d-flex justify-content-between align-items-center w-100 pt-2"
    >
      <div v-if="toEdit">
        <button
          id="saveMmceList"
          class="btn btn-success btn-xs p-2"
          @click="saveMmceList"
          :disabled="isSaving"
        >
          {{ isSaving ? "Saving..." : "Save Changes" }}
        </button>
        <button
          id="discardMmceChanges"
          class="ml-3 btn btn-danger btn-xs p-2"
          @click="discardChanges"
        >
          Discard
        </button>
      </div>

      <button
        v-else
        class="btn btn-warning btn-xs p-2"
        @click="toEdit = !toEdit"
      >
        Edit List
      </button>
      <h6 class="mb-0 pb-0">
        As of Today: <strong>{{ todayDate }}</strong>
      </h6>

      <button
        id="generateReport"
        class="ml-3 btn btn-outline-primary btn-xs p-2"
        @click="downloadExcelReport"
      >
        Generate Report
      </button>
    </div>

    <div class="col col-12">
      <div class="mt-3" id="mmceReportTbl">
        <div class="table-container">
          <table class="table table-bordered table-sm text-small">
            <thead>
              <tr>
                <th class="freeze-col freeze-col-1">No</th>
                <th class="freeze-col freeze-col-2">Vessel</th>
                <th class="freeze-col freeze-col-3">Rank</th>
                <th>Promoted</th>
                <th>At Presence</th>
                <th>Age</th>
                <th>Joined On</th>
                <th>Joined In</th>
                <th>Mos</th>
                <th>Est.off</th>
                <th>Reliever Candidate</th>
                <th>Age</th>
                <th>Ex-Vessel</th>
                <th>Predecessor</th>
                <th>Age</th>
                <th>Mos</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              <!-- Loading State -->
              <tr v-if="isLoadingData">
                <td colspan="17" class="text-center py-4">
                  <div class="d-flex justify-content-center align-items-center">
                    <div
                      class="spinner-border spinner-border-sm mr-2"
                      role="status"
                    >
                      <span class="sr-only">Loading...</span>
                    </div>
                    <span>Loading MMCE data...</span>
                  </div>
                </td>
              </tr>

              <!-- Data Rows -->
              <template v-else-if="reportData && reportData.length > 0">
                <template v-for="(vessel, index) in reportData">
                  <tr :key="'master-' + index">
                    <td class="freeze-col freeze-col-1"
                    :style="
                        (vessel.index_color_scheme && get(vessel, 'crew_composition_has_jpn_crew') != true)
                          ? `background-color: #${vessel.index_color_scheme};`
                          : ''
                      "
                    >{{ index + 1 }}</td>

                    <!-- vessel name -->
                    <td
                      class="freeze-col freeze-col-2 text-left pl-3 font-weight-bold"
                      :style="
                        (vessel.index_color_scheme && get(vessel, 'crew_composition_has_jpn_crew') == true)
                          ? `background-color: #${vessel.index_color_scheme};`
                          : ''
                      "
                    >
                      <span>{{ vessel.vessel_name }}</span>
                    </td>

                    <td class="freeze-col freeze-col-3"
                        :style="
                        get(vessel, 'master.onboard_promoted.is_promoted') ===
                        true
                          ? `background-color: #${get(
                              vessel,
                              'master.onboard_promoted.style'
                            )};`
                          : ''
                      "
                    >
                      {{ get(vessel, "master.rank") }}
                    </td>
                    <td
                      :style="
                        get(vessel, 'master.onboard_promoted.is_promoted') ===
                        true
                          ? `background-color: #${get(
                              vessel,
                              'master.onboard_promoted.style'
                            )};`
                          : ''
                      "
                    >
                      <span style="font-size: normal; font-weight: 600;">
                        {{
                          get(vessel, "master.onboard_promoted.is_promoted") ===
                          true
                            ? "*"
                            : ""
                        }}</span
                      >
                    </td>

                    <td
                      class="text-left gg"
                      :title="
                        isSystemAdmin ? get(vessel, 'master.onboard_id') : ''
                      "
                      :style="
                        get(vessel, 'master.new_embark.is_new_embark') === true
                          ? `background-color: #${get(
                              vessel,
                              'master.new_embark.style'
                            )};`
                          : get(vessel, 'master.onboard_promoted.is_promoted') === true ?
                          `background-color: #${get(
                              vessel,
                              'master.onboard_promoted.style'
                            )};` : ''
                      "
                    >
                      <router-link
                        :to="{
                          name: 'CrewActivity',
                          params: { id: get(vessel, 'master.onboard_id') },
                        }"
                        target="_blank"
                      >
                        {{ get(vessel, "master.onboard_name") }}
                      </router-link>
                    </td>
                    <td
                      :class="
                        get(vessel, 'master.onboard_age.over_age') === true
                          ? get(vessel, 'master.onboard_age.style')
                              .replace(/,\s?/g, ' ')
                              .toLowerCase()
                          : ''
                      "
                    >
                      {{ get(vessel, "master.onboard_age.age") }}
                    </td>
                    <td>{{ get(vessel, "master.join_date") }}</td>
                    <td style="font-size: smaller; ">{{ get(vessel, "master.join_place") }}</td>
                    <td>
                      <span
                        :style="
                          `color:#${get(vessel, 'master.onboard_mos.style')}; ${
                            get(vessel, 'master.onboard_mos.style') === ''
                              ? ''
                              : 'font-weight: bold;'
                          }`
                        "
                        >{{ get(vessel, "master.onboard_mos.mos") }}</span
                      >
                    </td>
                    <td>
                    <span v-if="!get(vessel, 'master')"></span>
                      <template v-else>
                        <span v-if="!toEdit">{{ get(vessel, "master.est_off") }}</span>
                        <input
                          v-else
                          maxlength="3"
                          minlength="2"
                          type="text"
                          v-model="vessel.master.est_off"
                          class="form-control form-control-sm"
                          style="width: 100%; max-width: 60px; text-align: center;"
                          placeholder="12A"
                          pattern="^\d{1,2}[A-Za-z]$"
                          title="Enter 1 or 2 digits followed by a letter (e.g., 1A or 12B)"
                        />
                      </template>
                    </td>
                    <td
                      :title="
                        isSystemAdmin && !toEdit
                          ? get(vessel, 'master.reliever_id')
                          : ''
                      "
                    >
                      <div v-if="toEdit">
                        <div
                          v-if="get(vessel, 'master.reliever_name')"
                          class="mb-1"
                        >
                          <small>{{
                            get(vessel, "master.reliever_name")
                          }}</small>
                        </div>
                        <div class="d-flex">
                          <button
                            class="btn btn-primary btn-xs py-1 px-2"
                            data-target="#mmceSearchCrewModal"
                            data-toggle="modal"
                            @click="openFindCrewModal(index, 'master', 1)"
                          >
                            {{
                              get(vessel, "master.reliever_name")
                                ? "Change"
                                : "Add Crew"
                            }}
                          </button>
                          <button
                            v-if="get(vessel, 'master.reliever_name')"
                            class="btn btn-danger btn-xs py-1 px-2 ml-1"
                            @click="removeRelieverCrew(index, 'master')"
                            title="Remove reliever crew"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <span v-else>
                        <router-link
                          :to="{
                            name: 'CrewActivity',
                            params: { id: get(vessel, 'master.reliever_id') },
                          }"
                          target="_blank"
                        >
                          {{ get(vessel, "master.reliever_name") }}
                        </router-link>
                      </span>
                    </td>
                    <td>{{ get(vessel, "master.reliever_age.age") }}</td>
                    <td>{{ get(vessel, "master.reliever_ex_vessel") }}</td>
                    <td
                      class="text-left"
                      :title="
                        isSystemAdmin
                          ? get(vessel, 'master.predecessor_id')
                          : ''
                      "
                        :style="
                            get(vessel, 'master.predecessor_experience_count') == 1
                            ? `background-color: #FFFF00;`
                          : ''
                        "

                    >
                      <router-link
                        :to="{
                          name: 'CrewActivity',
                          params: { id: get(vessel, 'master.predecessor_id') },
                        }"
                        target="_blank"
                        
                      >
                        {{ get(vessel, "master.predecessor_name") }}
                      </router-link>
                    </td>
                    <td
                      :class="
                        get(vessel, 'master.predecessor_age.over_age') === true
                          ? get(vessel, 'master.predecessor_age.style')
                              .replace(/,\s?/g, ' ')
                              .toLowerCase()
                          : ''
                      "
                    >
                      {{ get(vessel, "master.predecessor_age.age") }}
                    </td>
                    <td>{{ get(vessel, "master.predecessor_mos") }}</td>
                    <td v-if="get(vessel, 'master')">
                      <textarea
                        v-if="toEdit"
                        v-model="vessel.master.remarks"
                        class="form-control form-control-sm"
                        rows="2"
                        style="width: 100%;"
                      ></textarea>
                      <span v-else>{{ get(vessel, "master.remarks") }}</span>
                    </td>
                    <td v-else></td>
                  </tr>
                  <tr :key="'engineer-' + index">
                    <td class="freeze-col freeze-col-1" 
                    :style="
                        (vessel.index_color_scheme && get(vessel, 'crew_composition_has_jpn_crew') != true)
                          ? `background-color: #${vessel.index_color_scheme};`
                          : ''
                      "
                    ></td>
                    <td
                      class="freeze-col freeze-col-2 text-left pl-2"
                      :style="
                        (vessel.index_color_scheme && get(vessel, 'crew_composition_has_jpn_crew') === true)
                          ? `background-color: #${vessel.index_color_scheme};`
                          : ''
                      "
                    >
                      <span class="mx-2">{{ vessel.vessel_type_code }}</span>
                      <span class="mx-2">{{
                        vessel.vessel_flag_natl_code
                      }}</span>
                      <span class="mx-2">{{ vessel.crew_composition_string }}</span>
                    </td>
                    <td class="freeze-col freeze-col-3"
                        :style="
                        get(
                          vessel,
                          'chief_engineer.onboard_promoted.is_promoted'
                        ) === true
                          ? `background-color: #${get(
                              vessel,
                              'chief_engineer.onboard_promoted.style'
                            )};`
                          : ''
                      "
                    >
                      {{ get(vessel, "chief_engineer.rank") }}
                    </td>
                    <td
                      :style="
                        get(
                          vessel,
                          'chief_engineer.onboard_promoted.is_promoted'
                        ) === true
                          ? `background-color: #${get(
                              vessel,
                              'chief_engineer.onboard_promoted.style'
                            )};`
                          : ''
                      "
                    >
                      <span style="font-size: normal; font-weight: 600;">{{
                        get(
                          vessel,
                          "chief_engineer.onboard_promoted.is_promoted"
                        ) === true
                          ? "*"
                          : ""
                      }}</span>
                    </td>
                    <td
                      class="text-left"
                      :title="
                        isSystemAdmin
                          ? get(vessel, 'chief_engineer.onboard_id')
                          : ''
                      "
                      :style="
                        get(vessel, 'chief_engineer.new_embark.is_new_embark') === true
                          ? `background-color: #${get(
                              vessel,
                              'chief_engineer.new_embark.style'
                            )};`
                          : get(vessel, 'chief_engineer.onboard_promoted.is_promoted') === true ?
                          `background-color: #${get(
                              vessel,
                              'chief_engineer.onboard_promoted.style'
                            )};` : ''
                      "
                    >
                      <router-link
                        :to="{
                          name: 'CrewActivity',
                          params: {
                            id: get(vessel, 'chief_engineer.onboard_id'),
                          },
                        }"
                        target="_blank"
                      >
                        {{ get(vessel, "chief_engineer.onboard_name") }}
                      </router-link>
                    </td>
                    <td
                      :class="
                        get(vessel, 'chief_engineer.onboard_age.over_age') ===
                        true
                          ? get(vessel, 'chief_engineer.onboard_age.style')
                              .replace(/,\s?/g, ' ')
                              .toLowerCase()
                          : ''
                      "
                    >
                      {{ get(vessel, "chief_engineer.onboard_age.age") }}
                    </td>
                    <td>{{ get(vessel, "chief_engineer.join_date") }}</td>
                    <td style="font-size: smaller; ">{{ get(vessel, "chief_engineer.join_place") }}</td>
                    <td
                        :style="
                          `color:#${get(vessel, 'chief_engineer.onboard_mos.style')}; ${
                            get(vessel, 'chief_engineer.onboard_mos.style') === ''
                              ? ''
                              : 'font-weight: bold;'
                          }`
                        "
                    >
                      {{ get(vessel, "chief_engineer.onboard_mos.mos") }}
                    </td>
                    <td>
                      <span v-if="!get(vessel, 'chief_engineer')"></span>
                      <template v-else>
                        <span v-if="!toEdit">{{ get(vessel, "chief_engineer.est_off") }}</span>
                        <input
                          v-else
                          maxlength="3"
                          minlength="2"
                          type="text"
                          v-model="vessel.chief_engineer.est_off"
                          class="form-control form-control-sm"
                          style="width: 100%; max-width: 60px; text-align: center;"
                          placeholder="12A"
                          pattern="^\d{1,2}[A-Za-z]$"
                          title="Enter 1 or 2 digits followed by a letter (e.g., 1A or 12B)"
                        />
                      </template>
                    </td>
                    <td
                      :title="
                        isSystemAdmin && !toEdit
                          ? get(vessel, 'chief_engineer.reliever_id')
                          : ''
                      "
                    >
                      <div v-if="toEdit">
                        <div
                          v-if="get(vessel, 'chief_engineer.reliever_name')"
                          class="mb-1"
                        >
                          <small>{{
                            get(vessel, "chief_engineer.reliever_name")
                          }}</small>
                        </div>
                        <div class="d-flex">
                          <button
                            class="btn btn-primary btn-xs py-1 px-2"
                            data-target="#mmceSearchCrewModal"
                            data-toggle="modal"
                            @click="
                              openFindCrewModal(index, 'chief_engineer', 2)
                            "
                          >
                            {{
                              get(vessel, "chief_engineer.reliever_name")
                                ? "Change"
                                : "Add Crew"
                            }}
                          </button>
                          <button
                            v-if="get(vessel, 'chief_engineer.reliever_name')"
                            class="btn btn-danger btn-xs py-1 px-2 ml-1"
                            @click="removeRelieverCrew(index, 'chief_engineer')"
                            title="Remove reliever crew"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <span v-else>
                        <router-link
                          :to="{
                            name: 'CrewActivity',
                            params: {
                              id: get(vessel, 'chief_engineer.reliever_id'),
                            },
                          }"
                          target="_blank"
                        >
                          {{ get(vessel, "chief_engineer.reliever_name") }}
                        </router-link>
                      </span>
                    </td>
                    <td>
                      {{ get(vessel, "chief_engineer.reliever_age.age") }}
                    </td>
                    <td>
                      {{ get(vessel, "chief_engineer.reliever_ex_vessel") }}
                    </td>

                    <td
                      class="text-left"
                      :title="
                        isSystemAdmin
                          ? get(vessel, 'chief_engineer.predecessor_id')
                          : ''
                      "
                      :style="
                            get(vessel, 'chief_engineer.predecessor_experience_count') == 1
                            ? `background-color: #FFFF00;`
                          : ''
                        "
                    >
                      <router-link
                        :to="{
                          name: 'CrewActivity',
                          params: {
                            id: get(vessel, 'chief_engineer.predecessor_id'),
                          },
                        }"
                        target="_blank"
                        
                      >
                        {{ get(vessel, "chief_engineer.predecessor_name") }}
                      </router-link>
                    </td>
                    <td
                      :class="
                        get(
                          vessel,
                          'chief_engineer.predecessor_age.over_age'
                        ) === true
                          ? get(vessel, 'chief_engineer.predecessor_age.style')
                              .replace(/,\s?/g, ' ')
                              .toLowerCase()
                          : ''
                      "
                    >
                      {{ get(vessel, "chief_engineer.predecessor_age.age") }}
                    </td>
                    <td>{{ get(vessel, "chief_engineer.predecessor_mos") }}</td>
                    <td v-if="get(vessel, 'chief_engineer')">
                      <textarea
                        v-if="toEdit"
                        v-model="vessel.chief_engineer.remarks"
                        class="form-control form-control-sm"
                        rows="2"
                        style="width: 100%;"
                      ></textarea>
                      <span v-else>{{
                        get(vessel, "chief_engineer.remarks")
                      }}</span>
                    </td>
                    <td v-else></td>
                  </tr>
                </template>
              </template>

              <!-- Empty State -->
              <tr v-else-if="!isLoadingData">
                <td colspan="17" class="text-center py-4 text-muted">
                  No MMCE data available
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <FindCrewModal
      v-if="showModal"
      title="Search Crew"
      unique="mmceSearchCrewModal"
      :rank="selectedRankId"
      :visibility="showModal"
      @update_visibility="showModal = false"
      @return-crew="handleCrewSelect"
    />

    <download-excel-loading />
  </div>
</template>

<style scoped>
#mmceReportTbl .table-container {
  max-height: 600px;
  overflow: auto;
  position: relative;
  text-transform: uppercase;
}

#mmceReportTbl table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content; /* to avoid collapsing columns */
  min-width: 100%;
}

#mmceReportTbl thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
}
#mmceReportTbl thead .freeze-col-1,
#mmceReportTbl thead .freeze-col-2,
#mmceReportTbl thead .freeze-col-3 {
  position: sticky;
  top: 0;
  z-index: 5;
}

#mmceReportTbl th,
#mmceReportTbl td {
  min-width: 25px;
  white-space: nowrap;
  vertical-align: middle;
}

/* Sticky Left Columns */
#mmceReportTbl .freeze-col {
  position: sticky;
  background: #f8f9fa;
  z-index: 3;
}

#mmceReportTbl .freeze-col-1 {
  left: 0;
  z-index: 4;
  min-width: 32px;
}
#mmceReportTbl .freeze-col-2 {
  left: 30px;
  min-width: 330px;
  z-index: 4;
}
#mmceReportTbl .freeze-col-3 {
  left: 362px;
  z-index: 4;
}

/* border */
#mmceReportTbl table,
#mmceReportTbl th,
#mmceReportTbl td {
  border: 1px solid #dee2e6;
}
</style>
